/**
 * Generic render size specification for image rendering
 */

import { Size } from './Size';

/** Either an absolute pixel count or a percentage literal like "42%" */
export type DimToken = number | `${number}%`;

/** Optional, token-based spec supplied by the caller */
export interface SizeConstraints {
  minWidth?: DimToken;
  maxWidth?: DimToken;
  minHeight?: DimToken;
  maxHeight?: DimToken;
}

export type SizeSpec = SizeConstraints | Size;

/** Convert one token to concrete pixels */
const resolveToken = (t: DimToken | undefined, intrinsic: number): number | undefined => {
  if (typeof t === 'number') return t;
  if (t === undefined) return undefined;
  return (parseFloat(t) / 100) * intrinsic;
};

/**
 * MIP strategy for image scaling
 */
export enum MIP {
  NONE = 0,
  DOWN = 1,
  DOWN_OR_UP = 2
}

/**
 * Calculate the target size that fits within constraints while maintaining aspect ratio
 */
export function calculateFittedSize(
  intrinsicSize: Size,
  constraints: SizeConstraints,
  mip: MIP
): Size {
  // Resolve constraints to concrete pixels
  const resolvedConstraints = {
    minWidth: resolveToken(constraints.minWidth, intrinsicSize.width),
    maxWidth: resolveToken(constraints.maxWidth, intrinsicSize.width),
    minHeight: resolveToken(constraints.minHeight, intrinsicSize.height),
    maxHeight: resolveToken(constraints.maxHeight, intrinsicSize.height)
  };

  const { width: intrinsicWidth, height: intrinsicHeight } = intrinsicSize;

  // Calculate scale factors for each constraint
  let scale = 1;

  // Apply minimum constraints
  if (resolvedConstraints.minWidth !== undefined) {
    const minWidthScale = resolvedConstraints.minWidth / intrinsicWidth;
    scale = Math.max(scale, minWidthScale);
  }
  if (resolvedConstraints.minHeight !== undefined) {
    const minHeightScale = resolvedConstraints.minHeight / intrinsicHeight;
    scale = Math.max(scale, minHeightScale);
  }

  // Apply maximum constraints
  if (resolvedConstraints.maxWidth !== undefined) {
    const maxWidthScale = resolvedConstraints.maxWidth / intrinsicWidth;
    scale = Math.min(scale, maxWidthScale);
  }
  if (resolvedConstraints.maxHeight !== undefined) {
    const maxHeightScale = resolvedConstraints.maxHeight / intrinsicHeight;
    scale = Math.min(scale, maxHeightScale);
  }

  // The idea is that we want to minimize the number of scaled sizes we must
  // generate and cache. Pixel image formats like PNG should be cached at 100%,
  // 50%, 25%, etc. of their intrinsic size. Vector formats like SVG will also
  // be rendered to pixels, but they can be scaled up or down without loss of
  // quality. So we need to adjust the scale based on the MIP strategy.
  switch (mip) {
    case MIP.NONE:
      // No mip - just return the scaled size
      break;
    case MIP.DOWN:
      // Adjusted scale should be <=1.0, and align with the power of 2 that is
      // closest to but less than or equal to the target scale
      scale = Math.min(1, scale);
      if (scale > 0) {
        const log2Scale = Math.log2(scale);
        const floorLog2 = Math.floor(log2Scale);
        scale = Math.pow(2, floorLog2);
      }
      break;
    case MIP.DOWN_OR_UP:
      // Adjusted scale must align with the power of 2 that is closest to the
      // target scale, whether it is greater or less than 1.0
      if (scale > 0) {
        const log2Scale = Math.log2(scale);
        const roundedLog2 = Math.round(log2Scale);
        scale = Math.pow(2, roundedLog2);
      }
      break;
  }

  // Apply the scale to both dimensions
  const scaledWidth = intrinsicWidth * scale;
  const scaledHeight = intrinsicHeight * scale;

  return new Size(Math.round(scaledWidth), Math.round(scaledHeight));
}
